/**
 * @providesModule DataDogErrorUtils
 *
 * Utility functions for handling and serializing errors for Datadog logging.
 * These utilities ensure error objects are properly converted to strings
 * to avoid [object Object] appearing in Datadog logs.
 */

/**
 * Safely extracts a string error message from any error type.
 * Handles Error instances, objects with message/error properties, fetch response errors, and nested structures.
 *
 * @param error - The error to extract a message from (can be any type)
 * @returns A string representation of the error message
 *
 * @example
 * // Standard Error
 * getErrorMessage(new Error('Something went wrong')) // => 'Something went wrong'
 *
 * @example
 * // Object with message property
 * getErrorMessage({ message: 'API Error', code: 500 }) // => 'API Error'
 *
 * @example
 * // Fetch response error with _bodyInit
 * getErrorMessage({ _bodyInit: '{"message": "Not Found"}' }) // => 'Not Found'
 *
 * @example
 * // Nested error object
 * getErrorMessage({ error: { message: 'Nested error' } }) // => 'Nested error'
 */
export function getErrorMessage(error: unknown): string {
  if (error === null || error === undefined) {
    return 'Unknown error (null/undefined)';
  }

  // Handle primitive types
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'number' || typeof error === 'boolean') {
    return String(error);
  }

  // Handle Error instances
  if (error instanceof Error) {
    return error.message || error.toString();
  }

  // Handle objects
  if (typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;

    // Check common error message properties
    if (typeof errorObj.message === 'string' && errorObj.message) {
      return errorObj.message;
    }
    if (typeof errorObj.error === 'string' && errorObj.error) {
      return errorObj.error;
    }
    if (typeof errorObj.errorMessage === 'string' && errorObj.errorMessage) {
      return errorObj.errorMessage;
    }

    // Handle fetch response errors with _bodyInit
    if (typeof errorObj._bodyInit === 'string') {
      try {
        const bodyJson = JSON.parse(errorObj._bodyInit);
        if (Array.isArray(bodyJson) && bodyJson[0]?.message) {
          return bodyJson[0].message;
        }
        if (bodyJson.message) {
          return bodyJson.message;
        }
        if (bodyJson.error) {
          return bodyJson.error;
        }
      } catch {
        return errorObj._bodyInit;
      }
    }

    // Handle nested error object
    if (errorObj.error && typeof errorObj.error === 'object') {
      return getErrorMessage(errorObj.error);
    }

    // Fallback: try to stringify the object
    try {
      const stringified = JSON.stringify(error);
      // Avoid returning empty object strings
      if (stringified && stringified !== '{}') {
        return stringified;
      }
    } catch {
      // JSON.stringify failed
    }
  }

  // Final fallback
  try {
    return String(error);
  } catch {
    return 'Unknown error (stringify failed)';
  }
}

/**
 * Extracts error code from an error object if available.
 *
 * @param error - The error to extract a code from
 * @returns The error code or null if not found
 */
export function getErrorCode(error: unknown): string | number | null {
  if (error === null || error === undefined) {
    return null;
  }

  if (typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;
    if (errorObj.code !== undefined && errorObj.code !== null) {
      return errorObj.code as string | number;
    }
  }

  return null;
}

/**
 * Extracts HTTP status code from an error object if available.
 *
 * @param error - The error to extract a status code from
 * @returns The status code or null if not found
 */
export function getErrorStatusCode(error: unknown): number | null {
  if (error === null || error === undefined) {
    return null;
  }

  if (typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;

    // Check various status code property names
    if (typeof errorObj.statusCode === 'number') {
      return errorObj.statusCode;
    }
    if (typeof errorObj.status === 'number') {
      return errorObj.status;
    }
    // Some errors have code as HTTP status
    if (typeof errorObj.code === 'number' && errorObj.code >= 100 && errorObj.code < 600) {
      return errorObj.code;
    }
  }

  return null;
}

/**
 * Creates a flattened error info object suitable for Datadog logging.
 * Ensures all error fields are primitive types to avoid [object Object] in logs.
 *
 * @param error - The error to extract info from
 * @returns An object with flattened error fields
 */
export function getFlattenedErrorInfo(error: unknown): {
  errorMessage: string;
  errorCode: string | number | null;
  errorStatusCode: number | null;
} {
  return {
    errorMessage: getErrorMessage(error),
    errorCode: getErrorCode(error),
    errorStatusCode: getErrorStatusCode(error),
  };
}

